import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/anusha.css';
import Journal from './Journal';
import Gallery from './Gallery';
import Timeline from './Timeline';
import Dates from './Dates';
import Stars from './Stars';
import Letters from './Letters';
import Letterbox from './Letterbox';
import Goodies from './Goodies';
import Soundtrack from './Soundtrack';
import Nudges from './Nudges';
import MomentsFilm from './MomentsFilm';
import Fireworks from './Fireworks';
import store from './journalStore';
import { decorate, occasionLabel } from './datesMath';

/* Private page for Anusha 💕 — not linked anywhere on the site.
   Reachable only by typing the URL: <baseURL>/Anusha
   Phase 1: the proposal (the never-says-no gag).
   Phase 2: a tabbed app — 💕 Us (the love note + photos) and 📖 Journal. */

const PHOTOS = Array.from({ length: 12 }, (_, i) => `/anusha/photo-${i + 1}.jpg`);

const CAPTIONS = [
  'us 💕', 'my favourite 😍', 'always 🥰', 'my home 🏡',
  'partners in crime 🤝', 'forever 💍', 'my person 🫶', 'sunshine ☀️',
  'mine 💖', 'best day 🌸', 'you & me 💞', 'endless 🔁',
];

// Used only if the gallery is empty — keeps the film strip from ever looking bare.
const FALLBACK_MOMENTS = PHOTOS.map((src, i) => ({ src, caption: CAPTIONS[i] }));

const TEASES = [
  'Nope! 😏', "Can't catch me! 💨", 'Wrong button, silly 🙈',
  'Not today! 😘', 'This button is shy 🫣', 'Try again, cutie 😚',
  'Nice try! 💕', "It's allergic to you 🤧", 'Nuh-uh 🙅', "404: 'No' not found 😜",
  'Keep dreaming 😄', 'My answer for you is YES 💍',
];

const HEART_CHARS = ['💖', '💕', '💗', '💞', '🩷', '❤️', '🌸', '✨'];

const PASSCODE_KEY = 'anusha_passcode';
const MUSIC_KEY = 'anusha_music';
const SONG_SRC = '/anusha/our-song.m4a';
const TAP_HEARTS = ['💗', '💖', '💕', '✨'];

const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function AnushaPage() {
  // Always begin at the proposal after unlocking — passcode → "Do you love me?" → Us/Journal.
  const [phase, setPhase] = useState('propose'); // propose | app
  const [tab, setTab] = useState('us'); // us | stars | journal | gallery | timeline | days | letters | goodies
  const [justSaidYes, setJustSaidYes] = useState(false);
  // who's on this device (so notes/photos default to the right author)
  const [me, setMe] = useState(
    () => (typeof window !== 'undefined' ? localStorage.getItem('anusha_journal_me') || '' : '')
  );
  const pickMe = (name) => {
    try {
      localStorage.setItem('anusha_journal_me', name);
    } catch (e) {
      /* ignore */
    }
    setMe(name);
  };

  const [dodges, setDodges] = useState(0);
  const [noFloating, setNoFloating] = useState(false);
  const [noPos, setNoPos] = useState({ x: 0, y: 0 });
  const [tease, setTease] = useState('');
  const [burstKey, setBurstKey] = useState(0);
  const noRef = useRef(null);

  // ── music (starts on "Yes" — a user gesture, so autoplay rules are happy) ──
  const audioRef = useRef(null);
  const fadeRef = useRef(0);
  const [musicOn, setMusicOn] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(MUSIC_KEY) !== 'off'
  );
  const [musicPlaying, setMusicPlaying] = useState(false);

  const fadeTo = useCallback((target, ms) => {
    const a = audioRef.current;
    if (!a) return;
    clearInterval(fadeRef.current);
    const step = 60;
    const from = a.volume;
    const t0 = Date.now();
    fadeRef.current = setInterval(() => {
      const f = Math.min(1, (Date.now() - t0) / ms);
      a.volume = from + (target - from) * f;
      if (f >= 1) {
        clearInterval(fadeRef.current);
        if (target === 0) a.pause();
      }
    }, step);
  }, []);

  const startMusic = useCallback(() => {
    let a = audioRef.current;
    if (!a) {
      a = new Audio(SONG_SRC);
      a.loop = true;
      a.volume = 0;
      audioRef.current = a;
    }
    a.play()
      .then(() => {
        setMusicPlaying(true);
        fadeTo(0.5, 2600);
      })
      .catch(() => {
        /* file missing or autoplay blocked — stay silent, no drama */
      });
  }, [fadeTo]);

  const stopMusic = useCallback(() => {
    setMusicPlaying(false);
    fadeTo(0, 700);
  }, [fadeTo]);

  const toggleMusic = useCallback(() => {
    if (musicPlaying) {
      localStorage.setItem(MUSIC_KEY, 'off');
      setMusicOn(false);
      stopMusic();
    } else {
      localStorage.setItem(MUSIC_KEY, 'on');
      setMusicOn(true);
      startMusic();
    }
  }, [musicPlaying, startMusic, stopMusic]);

  // while one of *our* songs plays, the ambient instrumental steps aside
  const songWasPlaying = useRef(false);
  const onSongOpen = useCallback(() => {
    songWasPlaying.current = musicPlaying;
    if (musicPlaying) stopMusic();
  }, [musicPlaying, stopMusic]);
  const onSongClose = useCallback(() => {
    if (songWasPlaying.current && musicOn) startMusic();
    songWasPlaying.current = false;
  }, [musicOn, startMusic]);

  // stop the song (and its fade timer) when leaving the page
  useEffect(
    () => () => {
      clearInterval(fadeRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    },
    []
  );

  // ── tap-hearts: little hearts bloom where she touches ──
  const tapLayerRef = useRef(null);
  useEffect(() => {
    if (reducedMotion()) return undefined;
    let lastTap = 0;
    const onDown = (e) => {
      const layer = tapLayerRef.current;
      if (!layer || !e.target || !(e.target instanceof Element)) return;
      if (e.target.closest('button, a, input, textarea, label, .aj-ss, .aj-letter-overlay, .aj-sky')) return;
      const now = Date.now();
      if (now - lastTap < 150) return;
      lastTap = now;
      const burst = 2 + Math.floor(Math.random() * 2);
      for (let i = 0; i < burst; i += 1) {
        const s = document.createElement('span');
        s.className = 'aj-tap-heart';
        s.textContent = pick(TAP_HEARTS);
        s.style.left = `${e.clientX + rand(-10, 10)}px`;
        s.style.top = `${e.clientY + rand(-6, 6)}px`;
        s.style.setProperty('--dx', `${rand(-26, 26)}px`);
        s.style.setProperty('--ts', `${rand(13, 21)}px`);
        s.style.animationDelay = `${i * 70}ms`;
        layer.appendChild(s);
        setTimeout(() => s.remove(), 1500 + i * 70);
      }
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, []);

  // ── single passcode gate for the whole page ──
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(
    () => typeof window !== 'undefined' && !!localStorage.getItem(PASSCODE_KEY)
  );
  const [passcode, setPasscode] = useState('');
  const [lockInput, setLockInput] = useState('');
  const [lockErr, setLockErr] = useState('');
  const [lockBusy, setLockBusy] = useState(false);

  // ── big-day check: fireworks + banner when an occasion is today ──
  const [todayEvents, setTodayEvents] = useState([]);
  // today's "reason I love you" teaser for the Us tab (full jar lives in Goodies)
  const [dailyReason, setDailyReason] = useState(null);
  useEffect(() => {
    if (!unlocked || !passcode) return undefined;
    let gone = false;
    (async () => {
      try {
        const events = await store.events(passcode);
        if (gone) return;
        const now = new Date();
        setTodayEvents(events.map((ev) => decorate(ev, now)).filter((ev) => ev.kind === 'today'));
      } catch (e) {
        /* the sky falls silently */
      }
      try {
        const reasons = await store.reasons(passcode);
        if (gone || !reasons.length) return;
        const idx = Math.floor((Date.now() + new Date().getTimezoneOffset() * -60000) / 86400000) % reasons.length;
        setDailyReason({ text: reasons[idx].text, num: idx + 1, total: reasons.length });
      } catch (e) {
        /* no jar yet — no teaser */
      }
    })();
    return () => {
      gone = true;
    };
  }, [unlocked, passcode]);

  // Auto-unlock if this device already knows the passcode.
  useEffect(() => {
    const saved = localStorage.getItem(PASSCODE_KEY);
    if (!saved) {
      setChecking(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { ok } = await store.verify(saved);
        if (cancelled) return;
        if (ok) {
          setPasscode(saved);
          setUnlocked(true);
        } else {
          localStorage.removeItem(PASSCODE_KEY);
        }
      } catch (e) {
        /* ignore */
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const doUnlock = useCallback(
    async (e) => {
      e.preventDefault();
      setLockErr('');
      const candidate = lockInput.trim();
      if (!candidate) return;
      setLockBusy(true);
      try {
        const { ok } = await store.verify(candidate);
        if (ok) {
          localStorage.setItem(PASSCODE_KEY, candidate);
          setPasscode(candidate);
          setUnlocked(true);
        } else {
          setLockErr('That’s not our secret. Try again. 💔');
        }
      } catch (e2) {
        setLockErr('Something went wrong — try again in a moment.');
      } finally {
        setLockBusy(false);
      }
    },
    [lockInput]
  );

  // Privacy + romantic fonts + PWA identity — set at runtime so index.html /
  // the rest of the site stay untouched. While she's here, the page presents
  // itself as the installable "Us 💕" app (manifest + iOS home-screen metas).
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'For Anusha 💕';

    const added = [];
    const addEl = (tag, attrs) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
      return el;
    };

    addEl('meta', { name: 'robots', content: 'noindex, nofollow' });
    addEl('link', {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@500;600;700&family=Caveat:wght@600;700&display=swap',
    });

    // swap (or add) head tags the site already owns — first-in-tree wins for
    // these, so appending a second one isn't enough. Restored on unmount.
    const swapped = [];
    const swapEl = (selector, attr, value, create) => {
      const el = document.querySelector(selector);
      if (el) {
        swapped.push([el, attr, el.getAttribute(attr)]);
        el.setAttribute(attr, value);
      } else {
        create();
      }
    };
    swapEl('link[rel="manifest"]', 'href', '/anusha-manifest.json', () =>
      addEl('link', { rel: 'manifest', href: '/anusha-manifest.json' })
    );
    swapEl('link[rel="apple-touch-icon"]', 'href', '/anusha/icon-180.png', () =>
      addEl('link', { rel: 'apple-touch-icon', href: '/anusha/icon-180.png' })
    );
    swapEl('meta[name="theme-color"]', 'content', '#2a0a1e', () =>
      addEl('meta', { name: 'theme-color', content: '#2a0a1e' })
    );
    addEl('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    addEl('meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' });
    addEl('meta', { name: 'apple-mobile-web-app-title', content: 'Us 💕' });

    return () => {
      document.title = prevTitle;
      swapped.forEach(([el, attr, prev]) => {
        if (prev == null) el.removeAttribute(attr);
        else el.setAttribute(attr, prev);
      });
      added.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);

  const floatHearts = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => ({
        id: i,
        char: pick(HEART_CHARS),
        left: rand(2, 96),
        size: rand(16, 40),
        dur: rand(8, 16),
        delay: rand(0, 12),
        sway: rand(-40, 40),
        opacity: rand(0.35, 0.7),
      })),
    []
  );

  const burst = useMemo(
    () =>
      justSaidYes
        ? Array.from({ length: 44 }, (_, i) => {
            const angle = rand(0, Math.PI * 2);
            const dist = rand(120, Math.min(520, (typeof window !== 'undefined' ? window.innerWidth : 800) * 0.45));
            return {
              id: i,
              char: pick(HEART_CHARS),
              tx: Math.cos(angle) * dist,
              ty: Math.sin(angle) * dist,
              size: rand(18, 40),
              delay: rand(0, 0.35),
              rot: rand(-140, 140),
            };
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [justSaidYes, burstKey]
  );

  const dodge = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();

    const btn = noRef.current;
    const w = btn ? btn.offsetWidth : 110;
    const h = btn ? btn.offsetHeight : 50;
    const margin = 14;
    const maxX = Math.max(margin, window.innerWidth - w - margin);
    const maxY = Math.max(margin, window.innerHeight - h - margin);

    const px = e && e.clientX != null ? e.clientX : window.innerWidth / 2;
    const py = e && e.clientY != null ? e.clientY : window.innerHeight / 2;
    let x = 0;
    let y = 0;
    for (let attempt = 0; attempt < 8; attempt += 1) {
      x = rand(margin, maxX);
      y = rand(margin, maxY);
      if (Math.hypot(x + w / 2 - px, y + h / 2 - py) > 220) break;
    }

    setNoPos({ x, y });
    setNoFloating(true);
    setDodges((d) => d + 1);
    setTease(pick(TEASES));
  }, []);

  const sayYes = useCallback(() => {
    setJustSaidYes(true);
    setBurstKey((k) => k + 1);
    setTease('');
    setPhase('app');
    setTab('us');
    if (musicOn) startMusic(); // the hearts burst AND the music swells 🎵
  }, [musicOn, startMusic]);

  const replay = useCallback(() => {
    setJustSaidYes(false);
    setDodges(0);
    setNoFloating(false);
    setNoPos({ x: 0, y: 0 });
    setTease('');
    setPhase('propose');
  }, []);

  const yesScale = Math.min(1 + dodges * 0.22, 3.2);
  const noStyle = noFloating
    ? { left: `${noPos.x}px`, top: `${noPos.y}px`, opacity: Math.max(0.25, 1 - dodges * 0.06) }
    : undefined;

  return (
    <div className="anusha-page">
      {/* hearts that bloom under her fingertip */}
      <div className="aj-tap-layer" ref={tapLayerRef} aria-hidden="true" />

      {/* big-day magic: fireworks behind everything once she's in */}
      {unlocked && phase === 'app' && todayEvents.length > 0 && <Fireworks />}

      {/* music toggle, once unlocked */}
      {unlocked && (
        <button
          type="button"
          className={`aj-music-btn${musicPlaying ? ' is-on' : ''}`}
          onClick={toggleMusic}
          aria-label={musicPlaying ? 'pause our song' : 'play our song'}
          title={musicPlaying ? 'pause our song' : 'play our song'}
        >
          {musicPlaying ? '🎵' : '🔇'}
        </button>
      )}

      {/* faint blurred photo collage */}
      <div className="anusha-bg" aria-hidden="true">
        {PHOTOS.slice(0, 8).map((src) => (
          <img key={src} src={src} alt="" loading="lazy" />
        ))}
      </div>
      <div className="anusha-veil" aria-hidden="true" />

      {/* floating hearts */}
      <div className="anusha-hearts" aria-hidden="true">
        {floatHearts.map((hpt) => (
          <span
            key={hpt.id}
            className="anusha-float"
            style={{
              left: `${hpt.left}%`,
              '--s': `${hpt.size}px`,
              '--d': `${hpt.dur}s`,
              '--delay': `${hpt.delay}s`,
              '--sway': `${hpt.sway}px`,
              '--o': hpt.opacity,
            }}
          >
            {hpt.char}
          </span>
        ))}
      </div>

      {/* confetti burst right after she says yes */}
      {justSaidYes && (
        <div className="anusha-burst" key={burstKey} aria-hidden="true">
          {burst.map((b) => (
            <span
              key={b.id}
              style={{
                '--tx': `${b.tx}px`,
                '--ty': `${b.ty}px`,
                '--bs': `${b.size}px`,
                '--bd': `${b.delay}s`,
                '--rot': `${b.rot}deg`,
              }}
            >
              {b.char}
            </span>
          ))}
        </div>
      )}

      <div className="anusha-content">
        {checking ? (
          <div className="anusha-lock">
            <div className="anusha-lock-emoji" aria-hidden="true">💞</div>
            <p className="anusha-lock-sub">unlocking…</p>
          </div>
        ) : !unlocked ? (
          <form className="anusha-lock" onSubmit={doUnlock}>
            <div className="anusha-lock-emoji" aria-hidden="true">🔒💕</div>
            <h1 className="anusha-lock-title">For Anusha</h1>
            <p className="anusha-lock-sub">
              This little world is just for us.
              <br />
              Enter our secret to come in. 💞
            </p>
            <input
              type="password"
              className="aj-input"
              value={lockInput}
              autoFocus
              autoComplete="current-password"
              onChange={(e) => setLockInput(e.target.value)}
              placeholder="Our secret passcode"
            />
            {lockErr && <div className="aj-err">{lockErr}</div>}
            <button type="submit" className="aj-btn" disabled={lockBusy}>
              {lockBusy ? 'Opening…' : 'Open 💞'}
            </button>
          </form>
        ) : phase === 'propose' ? (
          <>
            <p className="anusha-eyebrow">a tiny, very important question</p>
            <h1 className="anusha-hi">Hey Anusha&nbsp;💕</h1>
            <p className="anusha-sub">
              I built an entire secret page just to ask you this.
              <br />
              (Yes, really. I may be a little extra. 🙈)
            </p>

            <h2 className="anusha-q">Do you love me?</h2>

            {dodges > 0 ? (
              <span className="anusha-tease" key={dodges}>
                {tease}
              </span>
            ) : (
              <p className="anusha-hint">psst… there's only one right answer 😇</p>
            )}

            <div className="anusha-btns">
              <button
                type="button"
                className="anusha-yes"
                style={{ '--yes-scale': yesScale }}
                onClick={sayYes}
                aria-label="Yes, I love you"
              >
                Yes 💗
              </button>

              <button
                ref={noRef}
                type="button"
                className={`anusha-no${noFloating ? ' is-floating' : ''}`}
                style={noStyle}
                onMouseEnter={dodge}
                onPointerDown={dodge}
                onPointerEnter={dodge}
                onTouchStart={dodge}
                onClick={dodge}
                onFocus={dodge}
                aria-label="No (good luck clicking this one)"
              >
                No
              </button>
            </div>
          </>
        ) : (
          <>
            {todayEvents.length > 0 && (
              <div className="aj-today-banner">
                <span aria-hidden="true">{todayEvents[0].emoji}</span>{' '}
                {occasionLabel(todayEvents[0])
                  ? `happy ${occasionLabel(todayEvents[0])}, you two`
                  : `today is ${todayEvents[0].title}`}
                {' '}
                <span aria-hidden="true">🎆</span>
              </div>
            )}

            <div className="anusha-tabs" role="tablist">
              <button
                type="button"
                className={`anusha-tab${tab === 'us' ? ' is-on' : ''}`}
                onClick={() => setTab('us')}
                role="tab"
                aria-selected={tab === 'us'}
              >
                💕 Us
              </button>
              <button
                type="button"
                className={`anusha-tab${tab === 'stars' ? ' is-on' : ''}`}
                onClick={() => setTab('stars')}
                role="tab"
                aria-selected={tab === 'stars'}
              >
                🌌 Stars
              </button>
              <button
                type="button"
                className={`anusha-tab${tab === 'journal' ? ' is-on' : ''}`}
                onClick={() => setTab('journal')}
                role="tab"
                aria-selected={tab === 'journal'}
              >
                📖 Journal
              </button>
              <button
                type="button"
                className={`anusha-tab${tab === 'gallery' ? ' is-on' : ''}`}
                onClick={() => setTab('gallery')}
                role="tab"
                aria-selected={tab === 'gallery'}
              >
                📸 Gallery
              </button>
              <button
                type="button"
                className={`anusha-tab${tab === 'timeline' ? ' is-on' : ''}`}
                onClick={() => setTab('timeline')}
                role="tab"
                aria-selected={tab === 'timeline'}
              >
                🕰️ Timeline
              </button>
              <button
                type="button"
                className={`anusha-tab${tab === 'days' ? ' is-on' : ''}`}
                onClick={() => setTab('days')}
                role="tab"
                aria-selected={tab === 'days'}
              >
                💖 Days
              </button>
              <button
                type="button"
                className={`anusha-tab${tab === 'letters' ? ' is-on' : ''}`}
                onClick={() => setTab('letters')}
                role="tab"
                aria-selected={tab === 'letters'}
              >
                💌 Letters
              </button>
              <button
                type="button"
                className={`anusha-tab${tab === 'goodies' ? ' is-on' : ''}`}
                onClick={() => setTab('goodies')}
                role="tab"
                aria-selected={tab === 'goodies'}
              >
                🎁 Goodies
              </button>
            </div>

            <div className="anusha-tabpanel">
              {tab === 'us' ? (
                <div className="anusha-reveal">
                  {justSaidYes && <h1 className="anusha-knew">I knew it! 💖</h1>}
                  <h2 className="anusha-love">I love you, Anusha</h2>

                  <div className="anusha-letter">
                    <p>Okay — real talk now. 🥹</p>
                    <p>
                      From the very first day, you turned my ordinary into extraordinary. Every car
                      ride became an adventure, every random afternoon became a favourite memory, and
                      every version of you — sleepy, silly, all dressed up, or just-woke-up — became
                      the one I can't stop smiling about.
                    </p>
                    <p>
                      You're my person, my safe place, my partner-in-crime, and the best thing that
                      has ever happened to this overly-complicated brain of mine. I am hopelessly,
                      ridiculously, permanently in love with you.
                    </p>
                    <p>
                      Thank you for being you. And thank you for clicking the only button that was
                      ever going to work. 😉
                    </p>
                  </div>

                  <p className="anusha-sign">
                    Forever yours,
                    <br />
                    Tanush 🤍
                  </p>

                  <p className="anusha-sub" style={{ marginTop: 18 }}>
                    a few of my favourite moments with you 👇
                  </p>

                  <MomentsFilm passcode={passcode} fallback={FALLBACK_MOMENTS} />

                  <Nudges passcode={passcode} me={me} onPickMe={pickMe} />

                  {dailyReason && (
                    <button type="button" className="aj-reason-teaser" onClick={() => setTab('goodies')}>
                      <span className="aj-reason-teaser-label">🫙 today&rsquo;s reason I love you</span>
                      <span className="aj-reason-teaser-text">“{dailyReason.text}”</span>
                      <span className="aj-reason-teaser-more">
                        #{dailyReason.num} of {dailyReason.total} · see the jar →
                      </span>
                    </button>
                  )}

                  <Soundtrack onOpen={onSongOpen} onClose={onSongClose} />

                  <button type="button" className="anusha-replay" onClick={replay}>
                    replay the question 🔁
                  </button>

                  <p className="aj-pwa-hint">
                    📲 psst — hit “Add to Home Screen” in your browser menu and we become an app.
                  </p>
                </div>
              ) : tab === 'stars' ? (
                <Stars passcode={passcode} />
              ) : tab === 'timeline' ? (
                <Timeline passcode={passcode} />
              ) : tab === 'days' ? (
                <Dates passcode={passcode} />
              ) : !me ? (
                <div className="anusha-whois">
                  <div className="anusha-lock-emoji" aria-hidden="true">💞</div>
                  <h2 className="anusha-whois-title">Who's on this phone?</h2>
                  <p className="anusha-sub">
                    Just once — so your notes &amp; photos land on the right side of our story. 💕
                  </p>
                  <div className="anusha-whois-btns">
                    <button type="button" className="anusha-whois-btn is-tanush" onClick={() => pickMe('Tanush')}>
                      I'm Tanush
                    </button>
                    <button type="button" className="anusha-whois-btn is-anusha" onClick={() => pickMe('Anusha')}>
                      I'm Anusha
                    </button>
                  </div>
                  <button type="button" className="anusha-whois-skip" onClick={() => pickMe('Us')}>
                    skip for now
                  </button>
                </div>
              ) : tab === 'journal' ? (
                <Journal passcode={passcode} />
              ) : tab === 'letters' ? (
                <div className="aj-letterstab">
                  <Letterbox passcode={passcode} me={me} />
                  <Letters />
                </div>
              ) : tab === 'goodies' ? (
                <Goodies passcode={passcode} me={me} />
              ) : (
                <Gallery passcode={passcode} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
